import { useCallback, useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Search } from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import { LengthInput, UnitToggle, productName, useUnits } from "../Architect/common";
import { SOLD_AS } from "../Architect/quantities";
import { formatLength } from "../Architect/units";
import { GST_RATES } from "../Quotations/gst";
import { capitalise } from "../utils/text";
import "../Moodboard/Moodboard.css";
import "../Architect/Architect.css";

const soldAsLabel = (key) => SOLD_AS.find((entry) => entry.key === key)?.label || key;

function TextCell({ value, placeholder, onSave, type = "text", maxLength = 120, label }) {
  const [text, setText] = useState(value ?? "");
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(value ?? "");
  }, [value]);
  return (
    <input
      type={type}
      value={text}
      maxLength={maxLength}
      min={type === "number" ? 0 : undefined}
      step={type === "number" ? "0.01" : undefined}
      placeholder={placeholder ?? ""}
      aria-label={label}
      onFocus={() => {
        focused.current = true;
      }}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        focused.current = false;
        if (String(text) !== String(value ?? "")) onSave(text === "" ? null : text);
      }}
      onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
    />
  );
}

// One editable set of details; `own` is what is saved here, `inherited` what
// shows through when a field is left blank.
function DetailFields({ own, inherited, units, onSave, withCode, soldAsFallback }) {
  const show = (key) => (inherited?.[key] != null ? String(inherited[key]) : "");
  const sizeHint = (key) => (inherited?.[key] ? formatLength(inherited[key], units) : undefined);
  return (
    <>
      {withCode && (
        <label className="ar-field">
          <span className="ar-field-label">Code</span>
          <TextCell value={own.code} placeholder={show("code")} maxLength={40} onSave={(code) => onSave({ code })} label="Code" />
        </label>
      )}
      <label className="ar-field">
        <span className="ar-field-label">Sold as</span>
        <select value={own.sold_as || ""} onChange={(event) => onSave({ sold_as: event.target.value || null })}>
          <option value="">Default ({soldAsLabel(inherited?.sold_as || soldAsFallback)})</option>
          {SOLD_AS.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </select>
      </label>
      <LengthInput label="Width / roll width" value={own.width_mm} units={units} onChange={(mm) => onSave({ width_mm: mm })} placeholder={sizeHint("width_mm")} />
      <LengthInput label="Height / roll length" value={own.height_mm} units={units} onChange={(mm) => onSave({ height_mm: mm })} placeholder={sizeHint("height_mm")} />
      <LengthInput label="Pattern repeat" value={own.repeat_mm} units={units} onChange={(mm) => onSave({ repeat_mm: mm })} placeholder={sizeHint("repeat_mm") || "none"} />
      <label className="ar-field">
        <span className="ar-field-label">Material</span>
        <TextCell value={own.material} placeholder={show("material")} onSave={(material) => onSave({ material })} label="Material" />
      </label>
      <label className="ar-field">
        <span className="ar-field-label">Finish</span>
        <TextCell value={own.finish} placeholder={show("finish")} onSave={(finish) => onSave({ finish })} label="Finish" />
      </label>
      <label className="ar-field">
        <span className="ar-field-label">Price (INR)</span>
        <TextCell type="number" value={own.price} placeholder={show("price")} onSave={(price) => onSave({ price })} label="Price" />
      </label>
      <label className="ar-field">
        <span className="ar-field-label">HSN code</span>
        <TextCell value={own.hsn} placeholder={show("hsn")} maxLength={8} onSave={(hsn) => onSave({ hsn })} label="HSN code" />
      </label>
      <label className="ar-field">
        <span className="ar-field-label">GST</span>
        <select value={own.gst_pct ?? ""} onChange={(event) => onSave({ gst_pct: event.target.value || null })}>
          <option value="">{inherited?.gst_pct != null ? `Default (${inherited.gst_pct}%)` : "Default (18%)"}</option>
          {GST_RATES.map((rate) => (
            <option key={rate} value={rate}>
              {rate}%
            </option>
          ))}
        </select>
      </label>
    </>
  );
}

export default function ProductDetails() {
  const [units, setUnits] = useUnits();
  const [defaults, setDefaults] = useState([]);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .get("/api/admin/product-defaults")
      .then(({ data }) => setDefaults(data))
      .catch(() => toast.error("Could not load category defaults."));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/api/admin/products", { params: { page, search: search || undefined, category } });
      setRows(data.items);
      setPages(data.total_pages);
      setTotal(data.total);
    } catch {
      toast.error("Could not load products.");
    } finally {
      setLoading(false);
    }
  }, [page, search, category]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const saveDefaults = async (name, fields) => {
    try {
      const { data } = await client.put(`/api/admin/product-defaults/${encodeURIComponent(name)}`, fields);
      setDefaults((current) => current.map((row) => (row.category.toLowerCase() === data.category ? { ...row, values: data.values } : row)));
      load();
      toast.success(`${capitalise(name)} defaults saved.`, { toastId: "defaults", autoClose: 1200 });
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save.");
    }
  };

  const saveProduct = async (fileId, fields) => {
    try {
      const { data } = await client.put(`/api/admin/products/${fileId}`, fields);
      setRows((current) => current.map((row) => (row.file_id === fileId ? { ...row, own: data.own, effective: data.effective } : row)));
      toast.success("Saved.", { toastId: "product", autoClose: 900 });
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save.");
    }
  };

  return (
    <main className="mb-page ar-page">
      <ToastContainer position="bottom-right" />
      <header className="mb-page-head">
        <div>
          <p className="mb-kicker">Administration</p>
          <h1>Product details</h1>
        </div>
        <UnitToggle units={units} onChange={setUnits} />
      </header>
      <p className="ar-muted ar-intro">
        These details appear on architects' quotations, spec sheets and in the calculator. Set defaults for each category
        once; anything left blank on a product uses its category's default. Changes save as soon as you leave a field.
      </p>

      <section className="ar-card">
        <h2>Category defaults</h2>
        <div className="ar-defaults">
          {defaults.map((row) => (
            <article key={row.category} className="ar-detail-row">
              <h3>{capitalise(row.category)}</h3>
              <div className="ar-detail-fields">
                <DetailFields
                  own={row.values}
                  inherited={null}
                  units={units}
                  soldAsFallback={row.suggested_sold_as}
                  onSave={(fields) => saveDefaults(row.category, fields)}
                />
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="ar-card">
        <div className="ar-card-head">
          <h2>Products · {total}</h2>
          <div className="ar-head-actions">
            <label className="ar-search">
              <Search size={15} strokeWidth={1.8} aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Search products"
              />
            </label>
            <select
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
                setPage(1);
              }}
              aria-label="Category"
            >
              <option value="all">All categories</option>
              {defaults.map((row) => (
                <option key={row.category} value={row.category.toLowerCase()}>
                  {capitalise(row.category)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {loading && !rows.length ? (
          <p className="ar-muted">Loading...</p>
        ) : (
          <div className="ar-defaults">
            {rows.map((row) => (
              <article key={row.file_id} className="ar-detail-row">
                <div className="ar-detail-product">
                  <img src={absoluteUrl(row.thumbnail_path) || undefined} alt="" loading="lazy" />
                  <div>
                    <strong>{productName(row)}</strong>
                    <span>{capitalise(row.category)}</span>
                  </div>
                </div>
                <div className="ar-detail-fields">
                  <DetailFields own={row.own} inherited={row.effective} units={units} withCode onSave={(fields) => saveProduct(row.file_id, fields)} />
                </div>
              </article>
            ))}
          </div>
        )}
        {pages > 1 && (
          <div className="ar-pager">
            <button type="button" className="mb-tool" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {pages}
            </span>
            <button type="button" className="mb-tool" disabled={page >= pages} onClick={() => setPage((value) => value + 1)}>
              Next
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
